import {
  EGlobalSearchItemType,
  type IGlobalSearchItem,
} from "@frontend-shared/types";
import { formatFileType } from "@frontend-shared/utils";
import type { LucideIcon } from "lucide-react";
import { CalendarClock, Webhook, Zap } from "lucide-react";
import type { ComponentType } from "react";

import { getModelIcon } from "@/api/parsers/modelParser";
import { getFileIcon } from "@/components/icons/file-icons";
import { resolveIntegrationIcon } from "@/components/icons/integrations/vendored-logo";
import { INTEGRATION_ICON_MAP } from "@/hooks/use-integration";

export enum EGlobalSearchLogoKind {
  // NOTE: A vendor's mark (model provider, integration) on a logo tile.
  BRAND = "brand",
  // NOTE: A coloured file-type glyph.
  FILE = "file",
  // NOTE: A plain glyph in the row's muted colour.
  GLYPH = "glyph",
}

export interface IGlobalSearchItemLogo {
  kind: EGlobalSearchLogoKind;
  Icon: ComponentType<{ className?: string }>;
  colorClassName?: string;
}

const AUTOMATION_TYPE_ICONS: Readonly<Record<string, LucideIcon>> = {
  cron: CalendarClock,
  webhook: Webhook,
  hook: Zap,
};

const getIntegrationLogo = (providerId: string): IGlobalSearchItemLogo => ({
  kind: EGlobalSearchLogoKind.BRAND,
  Icon: resolveIntegrationIcon({
    providerId,
    authored: INTEGRATION_ICON_MAP[providerId],
  }),
});

// NOTE: NSAI-6853 — the result's own picture where one exists: the model
// provider's mark (as on the Models page), the integration's logo (as on the
// Integrations page), the file-type glyph (as in Knowledge and AI Generated
// Files) and the kind of agent. `null` leaves the row on its sidebar /
// type icon. Resolved outside the component, as every icon lookup in the app
// is, so the react-compiler never sees a component chosen during render.
export const getGlobalSearchItemLogo = (
  item: IGlobalSearchItem,
): IGlobalSearchItemLogo | null => {
  switch (item.type) {
    case EGlobalSearchItemType.MODEL:
      return item.params.provider === undefined
        ? null
        : {
            kind: EGlobalSearchLogoKind.BRAND,
            Icon: getModelIcon({
              provider: item.params.provider,
              hostingProvider: item.params.hostingProvider,
            }),
          };
    case EGlobalSearchItemType.INTEGRATION_PROVIDER:
      return getIntegrationLogo(item.id);
    case EGlobalSearchItemType.INTEGRATION_CONNECTION:
      return item.params.provider === undefined
        ? null
        : getIntegrationLogo(item.params.provider);
    case EGlobalSearchItemType.GENERATED_FILE:
    case EGlobalSearchItemType.KNOWLEDGE_FILE: {
      if (item.params.mimeType === undefined) return null;
      const { Icon, color } = getFileIcon(item.params.mimeType);
      return { kind: EGlobalSearchLogoKind.FILE, Icon, colorClassName: color };
    }
    case EGlobalSearchItemType.AUTOMATION: {
      const Icon = AUTOMATION_TYPE_ICONS[item.params.automationType ?? ""];
      return Icon === undefined
        ? null
        : { kind: EGlobalSearchLogoKind.GLYPH, Icon };
    }
    default:
      return null;
  }
};

// NOTE: Files carry their raw MIME type as the subtitle; the row shows the
// same short label the file lists do ("PDF", "Word document").
export const getGlobalSearchItemSubtitle = (
  item: IGlobalSearchItem,
): string | null =>
  item.params.mimeType !== undefined && item.subtitle === item.params.mimeType
    ? formatFileType(item.params.mimeType)
    : item.subtitle;
